import React, { useState, useEffect } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const EXPIRY_OPTIONS = [
  { label: "Never Expire (Lifetime)", value: 0 },
  { label: "30 Days", value: 30 },
  { label: "60 Days", value: 60 },
  { label: "90 Days", value: 90 },
  { label: "180 Days", value: 180 },
  { label: "365 Days (1 Year)", value: 365 },
];

const Toggle = ({ enabled, onChange, id }) => (
  <button
    id={id}
    type="button"
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      enabled ? "bg-indigo-600" : "bg-gray-300"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const LoyaltyWalletSettings = ({ userId, settings, onUpdate }) => {
  const [loyalty, setLoyalty] = useState({
    enabled: settings?.loyaltySettings?.enabled || false,
    earningPercent: settings?.loyaltySettings?.earningPercent ?? 5,
    redemptionRate: settings?.loyaltySettings?.redemptionRate ?? 1,
    minRedeemPoints: settings?.loyaltySettings?.minRedeemPoints ?? 10,
    expiryDays: settings?.loyaltySettings?.expiryDays ?? 0,
  });

  const [wallet, setWallet] = useState({
    enabled: settings?.walletSettings?.enabled || false,
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setLoyalty({
        enabled: settings.loyaltySettings?.enabled || false,
        earningPercent: settings.loyaltySettings?.earningPercent ?? 5,
        redemptionRate: settings.loyaltySettings?.redemptionRate ?? 1,
        minRedeemPoints: settings.loyaltySettings?.minRedeemPoints ?? 10,
        expiryDays: settings.loyaltySettings?.expiryDays ?? 0,
      });
      setWallet({
        enabled: settings.walletSettings?.enabled || false,
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await api.put(
        `user/${userId}`,
        { loyaltySettings: loyalty, walletSettings: wallet },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Loyalty & Wallet settings saved! ✓");
      if (onUpdate) onUpdate({ loyaltySettings: loyalty, walletSettings: wallet });
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-lg">
          🎁
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Loyalty & Wallet</h2>
          <p className="text-sm text-gray-500">Configure rewards and prepaid wallet for your customers</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* ── Loyalty Points ── */}
        <div className={`rounded-xl border-2 p-5 transition-all duration-200 ${loyalty.enabled ? "border-indigo-300 bg-indigo-50" : "border-gray-200 bg-gray-50"}`}>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">⭐</span>
              <span className="font-bold text-gray-800 text-base">Loyalty Points</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{loyalty.enabled ? "Active" : "Inactive"}</span>
              <Toggle
                id="loyalty-toggle"
                enabled={loyalty.enabled}
                onChange={(val) => setLoyalty((p) => ({ ...p, enabled: val }))}
              />
            </div>
          </div>

          {loyalty.enabled && (
            <div className="space-y-4">
              {/* Earning % */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  🏆 Points Earn Rate (% of subtotal)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="earning-percent"
                    type="range"
                    min="1"
                    max="20"
                    step="0.5"
                    value={loyalty.earningPercent}
                    onChange={(e) =>
                      setLoyalty((p) => ({ ...p, earningPercent: Number(e.target.value) }))
                    }
                    className="flex-1 accent-indigo-600"
                  />
                  <span className="text-sm font-bold text-indigo-700 w-12 text-right">
                    {loyalty.earningPercent}%
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  On a ₹1000 bill → <strong>{Math.floor(1000 * loyalty.earningPercent / 100)} pts earned</strong>
                </p>
              </div>

              {/* Redemption Rate */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  💰 Redemption Rate (₹ per point)
                </label>
                <input
                  id="redemption-rate"
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={loyalty.redemptionRate}
                  onChange={(e) =>
                    setLoyalty((p) => ({ ...p, redemptionRate: Number(e.target.value) }))
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  100 pts = <strong>₹{(100 * loyalty.redemptionRate).toFixed(0)}</strong> discount
                </p>
              </div>

              {/* Min Redeem Points */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  🔒 Minimum Points to Redeem
                </label>
                <input
                  id="min-redeem-points"
                  type="number"
                  min="1"
                  value={loyalty.minRedeemPoints}
                  onChange={(e) =>
                    setLoyalty((p) => ({ ...p, minRedeemPoints: Number(e.target.value) }))
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Expiry */}
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  ⏳ Points Expiry Policy
                </label>
                <select
                  id="expiry-days"
                  value={loyalty.expiryDays}
                  onChange={(e) =>
                    setLoyalty((p) => ({ ...p, expiryDays: Number(e.target.value) }))
                  }
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {loyalty.expiryDays === 0 && (
                  <p className="text-xs text-green-600 mt-1 font-medium">♾️ Points never expire</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Wallet ── */}
        <div className={`rounded-xl border-2 p-5 transition-all duration-200 ${wallet.enabled ? "border-emerald-300 bg-emerald-50" : "border-gray-200 bg-gray-50"}`}>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">👛</span>
              <span className="font-bold text-gray-800 text-base">Prepaid Wallet</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{wallet.enabled ? "Active" : "Inactive"}</span>
              <Toggle
                id="wallet-toggle"
                enabled={wallet.enabled}
                onChange={(val) => setWallet({ enabled: val })}
              />
            </div>
          </div>

          {wallet.enabled ? (
            <div className="space-y-3">
              <div className="bg-emerald-100 rounded-lg p-4 text-sm text-emerald-800">
                <p className="font-semibold mb-1">✅ Wallet is enabled</p>
                <p>Customers can load balance and pay directly from their wallet at checkout. Staff can top-up wallet from the customer profile page.</p>
              </div>
              <ul className="text-xs text-gray-600 space-y-1 pl-2">
                <li>• Wallet balance shows on customer cards</li>
                <li>• Cashier can apply wallet during billing</li>
                <li>• Full transaction audit log maintained</li>
              </ul>
            </div>
          ) : (
            <div className="text-sm text-gray-400 mt-2">
              Enable wallet to allow customers to pre-load balance and pay faster at checkout.
            </div>
          )}
        </div>
      </div>

      {/* Preview Summary */}
      {(loyalty.enabled || wallet.enabled) && (
        <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-4">
          <p className="text-sm font-semibold text-indigo-700 mb-2">📋 Current Configuration Summary</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-center">
            {loyalty.enabled && (
              <>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-indigo-600 font-bold text-lg">{loyalty.earningPercent}%</div>
                  <div className="text-gray-500">Earn Rate</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-indigo-600 font-bold text-lg">₹{loyalty.redemptionRate}</div>
                  <div className="text-gray-500">Per Point</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-indigo-600 font-bold text-lg">{loyalty.minRedeemPoints}</div>
                  <div className="text-gray-500">Min Redeem</div>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-indigo-600 font-bold text-lg">
                    {loyalty.expiryDays === 0 ? "∞" : `${loyalty.expiryDays}d`}
                  </div>
                  <div className="text-gray-500">Expiry</div>
                </div>
              </>
            )}
            {wallet.enabled && !loyalty.enabled && (
              <div className="bg-white rounded-lg p-3 shadow-sm col-span-2">
                <div className="text-emerald-600 font-bold text-lg">Active</div>
                <div className="text-gray-500">Wallet</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          id="save-loyalty-wallet"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-60"
        >
          {saving ? (
            <>
              <span className="animate-spin">⟳</span>
              Saving...
            </>
          ) : (
            <>💾 Save Settings</>
          )}
        </button>
      </div>
    </div>
  );
};

export default LoyaltyWalletSettings;
